import type { ReactNode } from "react";

import type {
  CCFeedConsolePositioning,
  CCFeedConsolidated,
  CCFeedFlow,
  CCFeedImpact,
  CCFeedImpactPoint,
  CCFeedLiqCluster,
  CCFeedLiqMap,
  CCFeedLiquidation,
  CCFeedMove,
  CCFeedOIChange,
  CCFeedRange,
  CCFeedSummary,
  CCFeedTakerMinute,
  CCFeedVenue,
  CCFeedWall,
  CCFeedWalls,
} from "../api/types";
import { useCCFeed } from "../hooks/useCCFeed";
import { useNow } from "../hooks/useNow";
import {
  CROSSED_TITLE,
  STALE_AFTER_MS,
  bpText,
  btcText,
  fixedText,
  fundingCell,
  markCell,
  moveRow,
  num,
  oiCell,
  oiUnitLabel,
  price,
  pricePath,
  signedText,
  spanLabel,
  spreadLabel,
  summaryAgeMs,
  topCell,
  usdText,
  venueAge,
  type Cell,
} from "../lib/ccfeed";
import { formatAge, formatTime } from "../lib/format";
import { Panel } from "./Panel";

/**
 * Биржи актива по фиду crypto-console (ADR-0039).
 *
 * # Зачем панель на экране
 *
 * Окно Polymarket решается ценой актива, а цена движется на десятке бирж
 * сразу. Индекс в карточке рынка говорит, ГДЕ цена; эта панель — ПОЧЕМУ она
 * там: кто давил тейкером, снимали ли заявки, сколько стоит сдвинуть цену на
 * миллион дельты и не было ли каскада ликвидаций. Те же числа видит ИИ-судья:
 * сводка одна, и оператор с судьёй спорят об одном и том же.
 *
 * # Правило пустоты
 *
 * Каждое отсутствующее значение приходит с причиной в `*_why`, и панель
 * показывает прочерк с этой причиной в подсказке. Значение, которое ЕСТЬ, но
 * старше порога сервера, — серое с той же подсказкой: прятать его нельзя,
 * ставка финансирования «стареет» за 10 с, а меняется раз в час. Подчёркнутое
 * пунктиром — значит, у ячейки есть пояснение.
 */
export function CCFeedPanel({ asset }: { asset: string }) {
  const q = useCCFeed(asset);
  const s = q.data?.summary;
  const title = `Биржи ${asset}`;

  if (!s) {
    return (
      <Panel title={title} subtitle="фид crypto-console" dense>
        <p className="text-xs text-[var(--color-muted)]">
          {q.isError
            ? `раздел не загрузился: ${(q.error as Error).message}`
            : (q.data?.reason ?? "загрузка…")}
        </p>
      </Panel>
    );
  }

  return (
    <Panel
      title={title}
      subtitle="фид crypto-console"
      dense
      actions={<FeedStatus s={s} />}
    >
      <div className="flex flex-col gap-3 text-xs">
        <Consolidated c={s.consolidated} why={s.consolidated_why} />

        {/* Слева — биржи, справа — поток и разложение движения. Таблица бирж
            широкая и длинная (десяток строк), а поток и движения короткие:
            стопкой они вытягивали бы панель вдвое без пользы. */}
        <div className="grid gap-3 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <Venues venues={s.venues ?? []} />
          <div className="flex min-w-0 flex-col gap-3">
            <Flow flow={s.flow} why={s.flow_why} />
            <Moves moves={s.moves ?? []} />
          </div>
        </div>

        <div className="grid gap-3 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <Impact impact={s.impact} why={s.impact_why} />
          <History s={s} />
        </div>

        {/* Величины, которые считает сам crypto-console. Слева — рынок в целом
            (позиционирование, свёртки свечей), справа — уровни у цены (карта
            ликвидаций, стены). Те же строки видит ИИ-судья с раскладки r5. */}
        <div className="grid gap-3 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <div className="flex min-w-0 flex-col gap-3">
            <ConsolePositioning p={s.console_positioning} why={s.console_positioning_why} />
            <Ranges ranges={s.ranges ?? []} why={s.ranges_why} />
          </div>
          <div className="flex min-w-0 flex-col gap-3">
            <LiqMap m={s.liq_map} why={s.liq_map_why} />
            <Walls title="стены перпов у вершины" w={s.walls_near} why={s.walls_near_why} />
            <Walls title="стены перпов крупным шагом" w={s.walls_wide} why={s.walls_wide_why} />
          </div>
        </div>
      </div>
    </Panel>
  );
}

/**
 * Состояние фида в шапке. Лист с собственным тиком часов (правило
 * hooks/useNow.ts): опрос в фоновой вкладке встаёт, и без тика возраст сводки
 * застыл бы на «0 с» ровно тогда, когда она перестала обновляться.
 */
function FeedStatus({ s }: { s: CCFeedSummary }) {
  const now = useNow(1000);
  const age = summaryAgeMs(s.at, now);
  const late = !(age <= STALE_AFTER_MS);
  return (
    <div className="flex items-center gap-3 text-xs">
      {!s.connected && (
        <span className="text-[var(--color-warn)]" title="клиент фида трейдера не подключён к crypto-console">
          нет соединения с фидом
        </span>
      )}
      <span
        className={s.venues_live < s.venues_total ? "text-[var(--color-warn)]" : "text-[var(--color-muted)]"}
        title="у скольких бирж вершина пригодна сейчас"
      >
        бирж {s.venues_live}/{s.venues_total}
      </span>
      <span
        className={late ? "text-[var(--color-warn)]" : "text-[var(--color-muted)]"}
        title={`сводка от ${formatTime(s.at)}; трейдер кладёт её раз в секунду`}
      >
        обновлено {formatAge(age)} назад
      </span>
    </div>
  );
}

/** Значение с подсказкой: пунктир говорит, что подсказка есть. */
function Hint({
  title,
  children,
  className,
}: {
  title: string | undefined;
  children: ReactNode;
  className?: string | undefined;
}) {
  if (!title) return <span className={className}>{children}</span>;
  return (
    <span className={`underline decoration-dotted underline-offset-2 ${className ?? ""}`} title={title}>
      {children}
    </span>
  );
}

function CellView({ c }: { c: Cell }) {
  return (
    <Hint title={c.title} className={c.stale ? "text-[var(--color-muted)] opacity-60" : undefined}>
      {c.text}
    </Hint>
  );
}

function Missing({ why }: { why: string | undefined }) {
  return (
    <Hint title={why} className="text-[var(--color-muted)]">
      —
    </Hint>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <h3 className="mb-1 text-[10px] uppercase tracking-wide text-[var(--color-muted)]">{children}</h3>;
}

function Consolidated({ c, why }: { c: CCFeedConsolidated | undefined; why: string | undefined }) {
  if (!c) {
    return (
      <div className="flex gap-2">
        <span className="text-[var(--color-muted)]">сводная вершина</span>
        <Missing why={why} />
      </div>
    );
  }
  const spread = spreadLabel(c.spread_bp);
  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
      <span className="text-[var(--color-muted)]" title={`набор ${c.set}, уверенность: ${c.confidence}`}>
        сводная {c.set}
      </span>
      <span>
        <span className="text-[var(--color-muted)]">bid </span>
        <span className="num text-[var(--color-bid)]">{price(c.bid)}</span>
        <span className="text-[var(--color-muted)]"> {c.bid_venue}</span>
      </span>
      <span>
        <span className="text-[var(--color-muted)]">ask </span>
        <span className="num text-[var(--color-ask)]">{price(c.ask)}</span>
        <span className="text-[var(--color-muted)]"> {c.ask_venue}</span>
      </span>
      <span>
        <span className="text-[var(--color-muted)]">mid </span>
        <span className="num">{price(c.mid)}</span>
      </span>
      {spread.crossed ? (
        <Hint title={CROSSED_TITLE} className="text-[var(--color-warn)]">
          {spread.text}
        </Hint>
      ) : (
        <span className="num text-[var(--color-muted)]">{spread.text}</span>
      )}
    </div>
  );
}

const TH = "px-1.5 py-0.5 text-left font-normal text-[var(--color-muted)]";
const TD = "num px-1.5 py-0.5 whitespace-nowrap";

function Venues({ venues }: { venues: CCFeedVenue[] }) {
  return (
    <section className="min-w-0 overflow-x-auto">
      <SectionTitle>биржи</SectionTitle>
      <table className="w-full text-[11px]">
        <thead>
          <tr>
            <th className={TH}>биржа</th>
            <th className={TH}>bid / ask</th>
            <th className={TH}>спред</th>
            <th className={TH}>mark</th>
            <th className={TH}>финансирование</th>
            <th className={TH}>OI</th>
            <th className={TH}>возраст</th>
          </tr>
        </thead>
        <tbody>
          {venues.map((v) => (
            <tr key={`${v.venue}:${v.inst}`} className="border-t border-[var(--color-border)]">
              <td className="px-1.5 py-0.5 whitespace-nowrap" title={v.inst}>
                {v.venue}
                {/* Вид — только когда это не перп: перпов в таблице почти все,
                    и подпись на каждой строке приучила бы её не читать. */}
                {v.kind !== "perp" && <span className="ml-1 text-[var(--color-muted)]">{v.kind}</span>}
              </td>
              <td className={TD}>
                <CellView c={topCell(v)} />
              </td>
              <td className={`${TD} text-[var(--color-muted)]`}>{fixedText(v.spread_bp, 2)}</td>
              <td className={TD}>
                <CellView c={markCell(v)} />
              </td>
              <td className={TD}>
                <CellView c={fundingCell(v)} />
              </td>
              <td className={TD}>
                <CellView c={oiCell(v)} />
              </td>
              <td className={`${TD} text-[var(--color-muted)]`}>{venueAge(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function deltaClass(value: string | undefined): string {
  const n = num(value);
  if (n === null || n === 0) return "";
  return n > 0 ? "text-[var(--color-buy)]" : "text-[var(--color-sell)]";
}

function Flow({ flow, why }: { flow: CCFeedFlow | undefined; why: string | undefined }) {
  if (!flow) {
    return (
      <section>
        <SectionTitle>поток тейкеров</SectionTitle>
        <Missing why={why} />
      </section>
    );
  }
  const liq = new Map((flow.liquidations ?? []).map((l) => [l.span_s, l]));
  return (
    <section className="min-w-0 overflow-x-auto">
      <SectionTitle>поток тейкеров, BTC · {flow.set}</SectionTitle>
      <table className="w-full text-[11px]">
        <thead>
          <tr>
            <th className={TH}>окно</th>
            <th className={TH}>покупки</th>
            <th className={TH}>продажи</th>
            <th className={TH}>дельта</th>
            <th className={TH}>оборот</th>
            <th className={TH} title="ликвидации: объём закрытых лонгов / шортов, BTC">
              ликв. L / S
            </th>
          </tr>
        </thead>
        <tbody>
          {(flow.windows ?? []).map((w) => {
            const l = liq.get(w.span_s);
            return (
              <tr key={w.span_s} className="border-t border-[var(--color-border)]">
                <td className="px-1.5 py-0.5 whitespace-nowrap">
                  {spanLabel(w.span_s)}
                  {!w.complete && (
                    <Hint
                      title="crypto-console пометил окно неполным: объёмы за него могут быть занижены"
                      className="ml-1 text-[var(--color-warn)]"
                    >
                      неполное
                    </Hint>
                  )}
                </td>
                <td className={`${TD} text-[var(--color-buy)]`}>{btcText(w.buy)}</td>
                <td className={`${TD} text-[var(--color-sell)]`}>{btcText(w.sell)}</td>
                <td className={`${TD} ${deltaClass(w.delta)}`}>{signedText(w.delta, 3)}</td>
                <td className={`${TD} text-[var(--color-muted)]`}>{usdText(w.notional_usd)}</td>
                <td className={TD}>
                  {l ? (
                    <Hint title={l.reason || (l.complete ? undefined : "окно ликвидаций неполное")}>
                      {btcText(l.long)} / {btcText(l.short)}
                    </Hint>
                  ) : (
                    <Missing why={flow.liq_why} />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {flow.liq_coverage && <LiqCoverage c={flow.liq_coverage} />}
    </section>
  );
}

/**
 * Покрытие ликвидаций по OI. Без него ноль ликвидаций читается как штиль, а
 * это может быть молчание площадок, которые их не публикуют.
 */
function LiqCoverage({ c }: { c: NonNullable<CCFeedFlow["liq_coverage"]> }) {
  const roles: [string, string[] | null | undefined][] = [
    ["полный поток", c.full],
    ["выборка (не чаще раза в секунду)", c.sampled],
    ["частично", c.partial],
    ["только флаг в сделках", c.in_trades],
    ["не публикуют", c.not_published],
    ["неизвестно", c.unknown],
    ["OI неизвестен", c.oi_unknown],
  ];
  const title = roles
    .filter(([, v]) => (v ?? []).length > 0)
    .map(([k, v]) => `${k}: ${(v ?? []).join(", ")}`)
    .join("\n");
  return (
    <p className="mt-1 text-[11px] text-[var(--color-muted)]">
      <Hint title={[c.oi_why, title].filter(Boolean).join("\n") || undefined}>
        ликвидации видны по {fixedText(c.oi_covered_pct, 0)}% OI
        {c.oi_known_pct !== undefined && ` (OI известен у ${fixedText(c.oi_known_pct, 0)}%)`}
      </Hint>
    </p>
  );
}

function Moves({ moves }: { moves: CCFeedMove[] }) {
  return (
    <section className="min-w-0 overflow-x-auto">
      <SectionTitle>из чего сложилось движение, $</SectionTitle>
      <table className="w-full text-[11px]">
        <thead>
          <tr>
            <th className={TH}>окно</th>
            <th className={TH}>Δ цены</th>
            <th className={TH} title="рыночные покупки + рыночные продажи">тейкеры</th>
            <th className={TH} title="снятие лимитных заявок: биды + аски">снятие</th>
            <th className={TH} title="новые лимитные заявки: биды + аски">новые</th>
            <th className={TH} title="часть движения, не объяснённая ни потоком, ни стаканом">прочее</th>
          </tr>
        </thead>
        <tbody>
          {moves.map((m) => {
            const r = moveRow(m);
            if (r.why) {
              return (
                <tr key={m.window} className="border-t border-[var(--color-border)]">
                  <td className="px-1.5 py-0.5">{r.window}</td>
                  <td className={TD} colSpan={5}>
                    <Missing why={r.why} />
                  </td>
                </tr>
              );
            }
            return (
              <tr key={m.window} className="border-t border-[var(--color-border)]">
                <td className="px-1.5 py-0.5 whitespace-nowrap">
                  <Hint title={r.partial} className={r.partial ? "text-[var(--color-warn)]" : undefined}>
                    {r.window}
                  </Hint>
                </td>
                <td className={`${TD} font-medium ${deltaClass(m.mid_change)}`}>{r.mid}</td>
                <td className={TD}>
                  <Hint title={r.takersTitle}>{r.takers}</Hint>
                </td>
                <td className={TD}>
                  <Hint title={r.removalTitle}>{r.removal}</Hint>
                </td>
                <td className={TD}>
                  <Hint title={r.additionTitle}>{r.addition}</Hint>
                </td>
                <td className={`${TD} text-[var(--color-muted)]`}>{r.undefinedPart}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

/** Последняя точка, у которой величина известна: у свежих минут λ и E бывают пусты. */
function lastWith(points: CCFeedImpactPoint[], key: "lambda" | "e"): CCFeedImpactPoint | undefined {
  for (let i = points.length - 1; i >= 0; i--) {
    const p = points[i];
    if (p && num(p[key]) !== null) return p;
  }
  return undefined;
}

const MARK_TEXT: Record<string, string> = {
  absorption: "поглощение",
  vacuum: "вакуум",
};

function Impact({ impact, why }: { impact: CCFeedImpact | undefined; why: string | undefined }) {
  if (!impact) {
    return (
      <section>
        <SectionTitle>импакт потока</SectionTitle>
        <Missing why={why} />
      </section>
    );
  }
  const points = impact.points ?? [];
  const lam = lastWith(points, "lambda");
  const e = lastWith(points, "e");
  const pending = (p: CCFeedImpactPoint | undefined) => (p?.provisional ? " · минута не закрыта" : "");
  return (
    <section className="min-w-0">
      <SectionTitle>импакт потока · {points.length} мин</SectionTitle>
      <div className="mb-1 flex flex-wrap gap-x-4 gap-y-1">
        <span title={`б.п. на $1M дельты тейкеров${pending(lam)}`}>
          <span className="text-[var(--color-muted)]">λ </span>
          <span className="num">{fixedText(lam?.lambda, 2)}</span>
          <span className="text-[var(--color-muted)]"> б.п./$1M</span>
        </span>
        <span title={`эффективность: ≈1 норма, ≪1 поглощение, ≫1 цена прошла больше дельты${pending(e)}`}>
          <span className="text-[var(--color-muted)]">E </span>
          <span className="num">{fixedText(e?.e, 2)}</span>
        </span>
        <span title={impact.lambda_rel ? `λ отн.: ${impact.lambda_rel}` : undefined}>
          <span className="text-[var(--color-muted)]">λ EMA25/99 </span>
          <span className="num">
            {fixedText(impact.lambda_ema25, 2)} / {fixedText(impact.lambda_ema99, 2)}
          </span>
        </span>
      </div>
      <ImpactChart points={points} />
      <p className="mt-1 text-[10px] text-[var(--color-muted)]">
        столбики — дельта тейкеров за минуту, $ (зелёный — покупки) · линия — изменение цены, б.п. ·
        отметки сверху: <span className="text-[var(--color-accent)]">■</span> поглощение,{" "}
        <span className="text-[var(--color-warn)]">■</span> вакуум · бледная минута — не закрыта
      </p>
    </section>
  );
}

const CHART_W = 300;
const CHART_H = 64;

/**
 * Импакт по минутам: столбики — дельта, линия — доход.
 *
 * Обе величины от общего нуля посередине, каждая в СВОЁМ масштабе: доллары и
 * базисные пункты несоизмеримы, а смотрят на них ради совпадения знаков и
 * пиков — дельта есть, цена не пошла, это и есть поглощение.
 */
function ImpactChart({ points }: { points: CCFeedImpactPoint[] }) {
  if (points.length === 0) {
    return <p className="text-[var(--color-muted)]">точек нет</p>;
  }
  const mid = CHART_H / 2;
  const maxDelta = Math.max(...points.map((p) => Math.abs(num(p.delta_usd) ?? 0)), 1);
  const maxRet = Math.max(...points.map((p) => Math.abs(num(p.ret_bp) ?? 0)), 0.01);
  const step = CHART_W / points.length;
  const segs: string[] = [];
  let pen = false;
  points.forEach((p, i) => {
    const r = num(p.ret_bp);
    if (r === null) {
      pen = false;
      return;
    }
    const x = i * step + step / 2;
    const y = mid - (r / maxRet) * (mid - 4);
    segs.push(`${pen ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`);
    pen = true;
  });
  return (
    <svg
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      preserveAspectRatio="none"
      className="h-16 w-full"
      role="img"
      aria-label="импакт потока по минутам"
    >
      <line x1="0" y1={mid} x2={CHART_W} y2={mid} stroke="var(--color-border)" strokeWidth="0.5" />
      {points.map((p, i) => {
        const d = num(p.delta_usd);
        const h = d === null ? 0 : (Math.abs(d) / maxDelta) * (mid - 4);
        const x = i * step + step * 0.15;
        return (
          <g key={p.t} opacity={p.provisional ? 0.35 : 0.8} data-provisional={p.provisional || undefined}>
            <rect
              x={x}
              y={d !== null && d < 0 ? mid : mid - h}
              width={step * 0.7}
              height={h}
              fill={d !== null && d < 0 ? "var(--color-sell)" : "var(--color-buy)"}
            >
              <title>
                {`${formatTime(p.t)}${p.provisional ? " (не закрыта)" : ""}\n` +
                  `дельта ${usdText(p.delta_usd)} · оборот ${usdText(p.vol_usd)}\n` +
                  `цена ${signedText(p.ret_bp, 2)} б.п. · λ ${fixedText(p.lambda, 2)} · E ${fixedText(p.e, 2)}` +
                  (p.mark ? `\n${MARK_TEXT[p.mark] ?? p.mark}` : "")}
              </title>
            </rect>
            {p.mark && (
              <rect
                x={x}
                y={0}
                width={step * 0.7}
                height={3}
                fill={p.mark === "vacuum" ? "var(--color-warn)" : "var(--color-accent)"}
                data-mark={p.mark}
              />
            )}
          </g>
        );
      })}
      {segs.length > 1 && (
        <path
          d={segs.join(" ")}
          fill="none"
          stroke="var(--color-fg)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
}

function History({ s }: { s: CCFeedSummary }) {
  const prices = s.price ?? [];
  const path = pricePath(
    prices.map((p) => num(p.mid)),
    CHART_W,
    32,
  );
  const last = prices[prices.length - 1];
  return (
    <section className="flex min-w-0 flex-col gap-2">
      <div>
        <SectionTitle>цена 15 мин · медиана середин</SectionTitle>
        {path ? (
          <div className="flex items-center gap-2">
            <svg viewBox={`0 0 ${CHART_W} 32`} preserveAspectRatio="none" className="h-8 flex-1">
              <path d={path.d} fill="none" stroke="var(--color-accent)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            </svg>
            <div className="num w-20 shrink-0 text-right text-[10px] leading-tight">
              <div className="text-[var(--color-muted)]">{price(String(path.max))}</div>
              <div title={last ? `${formatTime(last.t)} · бирж ${last.venues}` : undefined}>{price(last?.mid)}</div>
              <div className="text-[var(--color-muted)]">{price(String(path.min))}</div>
            </div>
          </div>
        ) : (
          <p className="text-[var(--color-muted)]">ряда ещё нет</p>
        )}
      </div>
      <TakerBars taker={s.taker ?? []} />
      <Liquidations items={s.liquidations ?? []} />
      <OIChanges items={s.oi_change ?? []} />
    </section>
  );
}

/** Тейкеры по минутам: покупки вверх, продажи вниз, общий масштаб на обе стороны. */
function TakerBars({ taker }: { taker: CCFeedTakerMinute[] }) {
  if (taker.length === 0) return null;
  const H = 24;
  const mid = H / 2;
  const max = Math.max(...taker.flatMap((m) => [num(m.buy) ?? 0, num(m.sell) ?? 0]), 0.001);
  // Ширина под пятнадцать минут, а не под число точек: иначе в начале истории
  // три столбика растягивались бы на всю ширину и выглядели бы «всплеском».
  const slots = Math.max(15, taker.length);
  const step = CHART_W / slots;
  const off = (slots - taker.length) * step;
  return (
    <div>
      <SectionTitle>тейкеры по минутам, BTC</SectionTitle>
      <svg viewBox={`0 0 ${CHART_W} ${H}`} preserveAspectRatio="none" className="h-6 w-full">
        <line x1="0" y1={mid} x2={CHART_W} y2={mid} stroke="var(--color-border)" strokeWidth="0.5" />
        {taker.map((m, i) => {
          const b = ((num(m.buy) ?? 0) / max) * mid;
          const sl = ((num(m.sell) ?? 0) / max) * mid;
          const x = off + i * step + step * 0.15;
          return (
            <g key={m.t}>
              <title>{`${formatTime(m.t)}: покупки ${btcText(m.buy)} · продажи ${btcText(m.sell)}`}</title>
              <rect x={x} y={mid - b} width={step * 0.7} height={b} fill="var(--color-buy)" opacity={0.8} />
              <rect x={x} y={mid} width={step * 0.7} height={sl} fill="var(--color-sell)" opacity={0.8} />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** Сколько последних ликвидаций показывать: каскад виден по первым строкам. */
const LIQ_SHOWN = 5;

function Liquidations({ items }: { items: CCFeedLiquidation[] }) {
  const shown = items.slice(-LIQ_SHOWN).reverse();
  return (
    <div>
      <SectionTitle>
        ликвидации 15 мин{items.length > LIQ_SHOWN ? ` · последние ${LIQ_SHOWN} из ${items.length}` : ""}
      </SectionTitle>
      {shown.length === 0 ? (
        <p className="text-[var(--color-muted)]">ликвидаций нет</p>
      ) : (
        <ul className="num text-[11px]">
          {shown.map((l, i) => (
            <li key={`${l.t}:${l.venue}:${i}`} className="flex gap-2">
              <span className="text-[var(--color-muted)]">{formatTime(l.t)}</span>
              <span>{l.venue}</span>
              {/* Ликвидация лонга — это принудительная ПРОДАЖА, отсюда цвет
                  продажи, а не «плохо»: так же читается и поток рядом. */}
              <span
                className={l.side === "long" ? "text-[var(--color-sell)]" : "text-[var(--color-buy)]"}
                title="сторона закрытой позиции"
              >
                {l.side === "long" ? "лонг" : l.side === "short" ? "шорт" : l.side}
              </span>
              <span>{btcText(l.qty)} BTC</span>
              <span className="text-[var(--color-muted)]">@ {price(l.price)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OIChanges({ items }: { items: CCFeedOIChange[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <SectionTitle>открытый интерес за окно истории</SectionTitle>
      <ul className="num text-[11px]">
        {items.map((o) => {
          const from = num(o.from);
          const to = num(o.to);
          const diff = from !== null && to !== null ? String(to - from) : undefined;
          const unit = oiUnitLabel(o.unit);
          return (
            <li key={`${o.venue}:${o.inst}`} className="flex gap-2">
              <span className="w-24 shrink-0 truncate" title={o.inst}>
                {o.venue}
              </span>
              <Hint title={unit ? `с ${formatTime(o.since)}` : `с ${formatTime(o.since)}\nединица не подтверждена венью`}>
                {fixedText(o.from, 1)} → {fixedText(o.to, 1)}
                {unit && ` ${unit}`}
              </Hint>
              <span className={deltaClass(diff)}>({signedText(diff, 1)})</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Доля в процентах со знаком: «+0.12%». */
function pctText(value: string | undefined): string {
  const t = signedText(value, 2);
  return t === "—" ? t : `${t}%`;
}

/** Окно в минутах: 60 → «1 ч», 15 → «15 мин». */
function minutesLabel(min: number): string {
  return min > 0 && min % 60 === 0 ? `${min / 60} ч` : `${min} мин`;
}

const CROWDED_TEXT: Record<string, string> = {
  long: "перегружены лонги",
  short: "перегружены шорты",
  neutral: "без перекоса",
};

/**
 * Позиционирование по расчёту crypto-console: OI в долларах и funding,
 * взвешенный по нему. Считается только по площадкам с подтверждённой единицей
 * OI; исключённые названы в подсказке, иначе медиана двух бирж читалась бы
 * как рынок целиком.
 */
function ConsolePositioning({ p, why }: { p: CCFeedConsolePositioning | undefined; why: string | undefined }) {
  if (!p) {
    return (
      <section>
        <SectionTitle>позиционирование (crypto-console)</SectionTitle>
        <Missing why={why} />
      </section>
    );
  }
  const excluded = (p.excluded ?? []).map((e) => `${e.venue} ${e.inst} (${e.part}): ${e.reason}`).join("\n");
  const venues = (p.venues ?? [])
    .map((v) => `${v.venue}: OI ${usdText(v.oi_usd)}, funding ${fixedText(v.funding_bp8h, 2)} б.п./8 ч`)
    .join("\n");
  return (
    <section className="min-w-0">
      <SectionTitle>позиционирование (crypto-console) · {formatTime(p.as_of)}</SectionTitle>
      <div className="num flex flex-wrap gap-x-4 gap-y-0.5 text-[11px]">
        <span>
          OI{" "}
          <Hint title={[p.oi_why, `площадок: ${p.oi_venues}`, venues].filter(Boolean).join("\n")}>
            {usdText(p.oi_total_usd)}
          </Hint>
        </span>
        {(p.oi_change ?? []).map((c) => (
          <span key={c.span_s}>
            Δ {spanLabel(c.span_s)}{" "}
            {c.pct === undefined ? (
              <Missing why={c.why} />
            ) : (
              <span className={deltaClass(c.pct)}>{pctText(c.pct)}</span>
            )}
          </span>
        ))}
      </div>
      <div className="num flex flex-wrap gap-x-4 gap-y-0.5 text-[11px]">
        <span>
          funding{" "}
          {p.funding_bp8h === undefined ? (
            <Missing why={p.funding_why} />
          ) : (
            <Hint title={`«+» — платят лонги; площадок: ${p.funding_venues}`}>
              {signedText(p.funding_bp8h, 2)} б.п./8 ч
            </Hint>
          )}
        </span>
        <span>
          {p.crowded === undefined ? (
            <>
              перекос <Missing why={p.crowded_why} />
            </>
          ) : (
            <Hint
              title={p.crowded_threshold_bp8h ? `порог ${p.crowded_threshold_bp8h} б.п./8 ч` : undefined}
              className={p.crowded === "neutral" ? undefined : "text-[var(--color-warn)]"}
            >
              {CROWDED_TEXT[p.crowded] ?? p.crowded}
            </Hint>
          )}
        </span>
        <span>
          базис {p.basis_bp === undefined ? <Missing why={p.basis_why} /> : bpText(p.basis_bp)}
        </span>
        {(p.excluded ?? []).length > 0 && (
          <Hint title={excluded} className="text-[var(--color-muted)]">
            исключено: {p.excluded.length}
          </Hint>
        )}
      </div>
    </section>
  );
}

/** Свёртки минутных свечей: сводный VWAP по площадкам за 15 мин, 1 ч, 4 ч. */
function Ranges({ ranges, why }: { ranges: CCFeedRange[]; why: string | undefined }) {
  if (ranges.length === 0) {
    return (
      <section>
        <SectionTitle>свечи</SectionTitle>
        <Missing why={why} />
      </section>
    );
  }
  return (
    <section className="min-w-0 overflow-x-auto">
      <SectionTitle>свечи · сводный VWAP</SectionTitle>
      <table className="w-full text-[11px]">
        <thead>
          <tr>
            <th className={TH}>окно</th>
            <th className={TH}>откр → закр</th>
            <th className={TH}>Δ</th>
            <th className={TH}>макс / мин</th>
            <th className={TH} title="тейкеры: покупки / продажи, $">тейкеры $</th>
            <th className={TH} title="ликвидации: лонги / шорты, $">ликв. L / S $</th>
          </tr>
        </thead>
        <tbody>
          {ranges.map((r) => {
            const gaps = r.price_minutes < r.minutes;
            // Сумма части минут — не объём окна: одна минута из шестидесяти
            // выглядела бы как спокойный час. Неполноту видно цветом и словами.
            const volumeNote = [
              r.volume_minutes < r.minutes ? `объём известен за ${r.volume_minutes} из ${r.minutes} мин` : "",
              (r.usd_partial_minutes ?? 0) > 0
                ? `в ${r.usd_partial_minutes} мин не все биржи: сумма — нижняя граница`
                : "",
              r.usd_why ?? "",
            ]
              .filter(Boolean)
              .join("\n");
            return (
              <tr key={r.span_min} className="border-t border-[var(--color-border)]">
                <td className="px-1.5 py-0.5 whitespace-nowrap">
                  <Hint
                    title={`с ${formatTime(r.from)}; свечей ${r.minutes}, с ценой ${r.price_minutes}, с объёмом ${r.volume_minutes}`}
                    className={gaps ? "text-[var(--color-warn)]" : undefined}
                  >
                    {minutesLabel(r.span_min)}
                  </Hint>
                </td>
                <td className={TD}>
                  {price(r.open)} → {price(r.close)}
                </td>
                <td className={`${TD} ${deltaClass(String(r.change_bp))}`}>{signedText(String(r.change_bp), 1)} б.п.</td>
                <td className={TD}>
                  <Hint title={`максимум в ${formatTime(r.high_at)}, минимум в ${formatTime(r.low_at)}`}>
                    {price(r.high)} / {price(r.low)}
                  </Hint>
                </td>
                <td className={TD}>
                  {r.volume_minutes === 0 ? (
                    <Missing why={volumeNote} />
                  ) : (
                    <Hint title={volumeNote || undefined} className={volumeNote ? "opacity-60" : undefined}>
                      <span className="text-[var(--color-buy)]">{usdText(r.buy_usd)}</span> /{" "}
                      <span className="text-[var(--color-sell)]">{usdText(r.sell_usd)}</span>
                    </Hint>
                  )}
                </td>
                <td className={TD}>
                  <Hint
                    title={r.liq_complete ? undefined : "поток ликвидаций за окно неполный: суммы занижены"}
                    className={r.liq_complete ? undefined : "text-[var(--color-warn)]"}
                  >
                    {usdText(r.liq_long_usd)} / {usdText(r.liq_short_usd)}
                  </Hint>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function ClusterRow({ label, c, why }: { label: string; c: CCFeedLiqCluster | undefined; why?: string | undefined }) {
  return (
    <li className="flex gap-2">
      <span className="w-24 shrink-0 text-[var(--color-muted)]">{label}</span>
      {c ? (
        <>
          <span>
            {price(c.px_from)}–{price(c.px_to)}
          </span>
          <span className="text-[var(--color-muted)]">{signedText(c.dist_bp, 0)} б.п.</span>
          <span>{usdText(c.usd)}</span>
        </>
      ) : (
        <Missing why={why} />
      )}
    </li>
  );
}

/**
 * Модельная карта ликвидаций: где по приросту OI и лестнице плеч должны лежать
 * уровни принудительного закрытия. Это модель, а не позиции, — так и подписано.
 */
function LiqMap({ m, why }: { m: CCFeedLiqMap | undefined; why: string | undefined }) {
  if (!m) {
    return (
      <section>
        <SectionTitle>карта ликвидаций (модель)</SectionTitle>
        <Missing why={why} />
      </section>
    );
  }
  return (
    <section className="min-w-0">
      <SectionTitle>
        карта ликвидаций (модель) · {formatTime(m.as_of)}
        {m.partial && (
          <Hint title={m.partial_why ?? "история модели неполная"} className="ml-1 normal-case text-[var(--color-warn)]">
            неполная
          </Hint>
        )}
      </SectionTitle>
      <ul className="num text-[11px]">
        <ClusterRow label="ближе снизу" c={m.nearest_below} why={m.nearest_below_why} />
        <ClusterRow label="ближе сверху" c={m.nearest_above} why={m.nearest_above_why} />
        <ClusterRow label="крупнее снизу" c={m.largest_below} />
        <ClusterRow label="крупнее сверху" c={m.largest_above} />
      </ul>
      <p className="num text-[10px] text-[var(--color-muted)]">
        <Hint
          title={`площадки: ${(m.venues ?? []).join(", ") || "—"}; исключено ${m.excluded}; минут истории ${m.minutes_used}` +
            (m.cluster_usd ? `; кластер — от ${usdText(m.cluster_usd)}` : "")}
        >
          лонги {usdText(m.long_total_usd)} · шорты {usdText(m.short_total_usd)} · середина {price(m.mid)}
        </Hint>
      </p>
    </section>
  );
}

function WallList({ items, sell }: { items: CCFeedWall[]; sell: boolean }) {
  if (items.length === 0) return <span className="text-[var(--color-muted)]">—</span>;
  return (
    <ul className={sell ? "text-[var(--color-sell)]" : "text-[var(--color-buy)]"}>
      {items.map((w) => (
        <li key={w.price}>
          <Hint title={`${btcText(w.qty)} BTC; бирж ${w.venues} из ${w.coverage}`}>
            {price(w.price)} <span className="text-[var(--color-muted)]">{signedText(w.dist_bp, 1)}</span>{" "}
            {usdText(w.usd)}
          </Hint>
        </li>
      ))}
    </ul>
  );
}

/** До трёх крупнейших строк сводного стакана перпов на сторону. */
function Walls({ title, w, why }: { title: string; w: CCFeedWalls | undefined; why: string | undefined }) {
  if (!w || (w.bids ?? []).length + (w.asks ?? []).length === 0) {
    return (
      <section>
        <SectionTitle>{title}</SectionTitle>
        <Missing why={w?.why ?? why} />
      </section>
    );
  }
  return (
    <section className="min-w-0">
      <SectionTitle>
        {title} · шаг {w.step} · бирж {w.venues}
      </SectionTitle>
      <div className="num grid grid-cols-2 gap-2 text-[11px]">
        <WallList items={w.bids ?? []} sell={false} />
        <WallList items={w.asks ?? []} sell />
      </div>
    </section>
  );
}
